import React, { useEffect, useState, useRef } from 'react';
import { useStrategyStore } from '../store';
import { strategiesAPI, signalsAPI } from '../services/api';
import Watchlist from '../components/Watchlist';
import ScanSettings from '../components/ScanSettings';
import './Dashboard.css';

function Dashboard() {
  const strategies = useStrategyStore((state) => state.strategies);
  const setStrategies = useStrategyStore((state) => state.setStrategies);
  const [recentSignals, setRecentSignals] = useState([]);
  const intervalRef = useRef(null);
  const abortRef = useRef(null);

  useEffect(() => {
    fetchStrategies();
    fetchRecentSignals();
    
    // Auto-refresh signals every 30 seconds
    intervalRef.current = setInterval(() => {
      fetchRecentSignals();
    }, 30000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      if (abortRef.current) {
        abortRef.current.abort();
      }
    };
  }, []);

  const fetchStrategies = async () => {
    try {
      const response = await strategiesAPI.list();
      setStrategies(response.data);
    } catch (error) {
      console.error('Failed to fetch strategies:', error);
    }
  };

  const fetchRecentSignals = async () => {
    if (abortRef.current) {
      abortRef.current.abort();
    }
    abortRef.current = new AbortController();

    try {
      const response = await signalsAPI.list({ limit: 5 }, { signal: abortRef.current.signal });
      setRecentSignals(response.data);
    } catch (error) {
      if (error.name === 'CanceledError' || error.code === 'ERR_CANCELED') return;
      console.error('Failed to fetch recent signals:', error);
    }
  };

  return (
    <div className="dashboard">
      <h2>Dashboard</h2>
      <div className="dashboard-grid">
        <div className="dashboard-card">
          <h3>Active Strategies</h3>
          <p className="stat-value">{strategies.filter(s => s.is_active).length}</p>
        </div>
        <div className="dashboard-card">
          <h3>Total Strategies</h3>
          <p className="stat-value">{strategies.length}</p>
        </div>
        <div className="dashboard-card">
          <h3>Portfolio Value</h3>
          <p className="stat-value">$0.00</p>
        </div>
        <div className="dashboard-card">
          <h3>P&L</h3>
          <p className="stat-value profit">+$0.00</p>
        </div>
      </div>

      <div className="dashboard-content">
        <section className="strategies-section">
          <h3>Your Strategies</h3>
          {strategies.length === 0 ? (
            <p>No strategies created yet. <a href="/strategies">Create one now</a></p>
          ) : (
            <div className="strategies-list">
              {strategies.map((strategy) => (
                <div key={strategy.id} className="strategy-item">
                  <h4>{strategy.strategy_name}</h4>
                  <p>Status: <strong>{strategy.is_active ? 'Active' : 'Inactive'}</strong></p>
                  <p>Created: {new Date(strategy.created_at).toLocaleDateString()}</p>
                </div>
              ))}
            </div>
          )}
        </section>

        <aside className="sidebar-widgets">
          <Watchlist />
          
          <div className="recent-signals-widget">
            <h3>Recent Signals</h3>
            {recentSignals.length === 0 ? (
              <p className="empty-text">No signals yet</p>
            ) : (
              <div className="recent-signals-list">
                {recentSignals.map((signal) => (
                  <div key={signal.id} className={`recent-signal-item signal-${signal.signal_type.toLowerCase()}`}>
                    <div className="signal-header-compact">
                      <span className={`signal-badge-small signal-${signal.signal_type.toLowerCase()}`}>
                        {signal.signal_type}
                      </span>
                      <strong>{signal.symbol}</strong>
                    </div>
                    <div className="signal-details-compact">
                      <span className="signal-price">${signal.price}</span>
                      <span className="signal-time">
                        {new Date(signal.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <ScanSettings />
        </aside>
      </div>
    </div>
  );
}

export default Dashboard;
